import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";

export const schedulePalette = {
    ink: "#2c3e50",
    header: "#34495e",
    ocean: "#3498db",
    sky: "#d6eaf8",
    warm: "#e67e22",
    sand: "#fdebd0",
    border: "#bdc3c7",
    surface: "#f8fbfd",
    muted: "#5f6f81",
};

const defaultAppFontFamily = "var(--app-font-family, 'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif)";
const defaultTableFontFamily = "var(--app-table-font-family, 'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif)";

const panelTone = {
    cool: {
        accent: schedulePalette.ocean,
        background: "linear-gradient(180deg, rgba(214, 234, 248, 0.72) 0%, rgba(255, 255, 255, 0.98) 100%)",
    },
    warm: {
        accent: schedulePalette.warm,
        background: "linear-gradient(180deg, rgba(253, 235, 208, 0.82) 0%, rgba(255, 255, 255, 0.98) 100%)",
    },
    neutral: {
        accent: schedulePalette.header,
        background: "linear-gradient(180deg, rgba(236, 240, 241, 0.92) 0%, rgba(255, 255, 255, 0.98) 100%)",
    },
};

export function PageShell({ children }) {
    return (
        <Stack
            spacing={2.5}
            sx={{
                position: "relative",
                isolation: "isolate",
                minHeight: "100%",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: "-20px -12px auto",
                    height: 220,
                    borderRadius: 6,
                    background:
                        "var(--app-page-shell-gradient, linear-gradient(135deg, rgba(214, 234, 248, 0.92) 0%, rgba(236, 240, 241, 0.84) 52%, rgba(253, 235, 208, 0.9) 100%))",
                    zIndex: -1,
                    pointerEvents: "none",
                },
            }}
        >
            {children}
        </Stack>
    );
}

export function PageHeader({ title, subtitle, actions }) {
    return (
        <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
            sx={{
                px: { xs: 0.5, md: 1 },
                pt: { xs: 0.5, md: 1 },
            }}
        >
            <Stack spacing={0.75}>
                <Typography sx={pageTypography.pageTitle}>
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ ...pageTypography.pageSubtitle, maxWidth: 720 }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </Stack>
            {actions ? <Box sx={{ width: { xs: "100%", lg: "auto" } }}>{actions}</Box> : null}
        </Stack>
    );
}

export function PagePanel({ children, tone = "neutral", sx = {}, ...props }) {
    const toneStyle = panelTone[tone] || panelTone.neutral;

    return (
        <Paper
            {...props}
            sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                border: `1px solid ${schedulePalette.border}`,
                borderTop: `4px solid ${toneStyle.accent}`,
                boxShadow: "0 18px 40px rgba(44, 62, 80, 0.10)",
                background: toneStyle.background,
                ...sx,
            }}
        >
            {children}
        </Paper>
    );
}

export const dataGridSx = {
    border: 0,
    backgroundColor: "transparent",
    fontFamily: "var(--app-table-font-family, 'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif)",
    fontSize: "var(--app-table-font-size, 0.875rem)",
    "& .MuiDataGrid-main": {
        borderRadius: 2,
        overflow: "hidden",
    },
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: schedulePalette.header,
        color: "#ffffff",
        borderBottom: `1px solid ${schedulePalette.ink}`,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 700,
        fontSize: "var(--app-table-header-font-size, 0.82rem)",
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "rgba(214, 234, 248, 0.24)",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "rgba(214, 234, 248, 0.5)",
    },
    "& .MuiDataGrid-cell": {
        borderColor: "rgba(189, 195, 199, 0.55)",
        color: "var(--app-table-cell-text-color, #3498db)",
        fontWeight: 700,
        fontFamily: "inherit",
        fontSize: "var(--app-table-font-size, 0.875rem)",
    },
    "& .MuiDataGrid-cellContent": {
        color: "var(--app-table-cell-text-color, #3498db)",
        fontWeight: 700,
        fontFamily: "inherit",
        fontSize: "var(--app-table-font-size, 0.875rem)",
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: `1px solid ${schedulePalette.border}`,
        backgroundColor: "rgba(236, 240, 241, 0.85)",
    },
    "& .MuiDataGrid-toolbarContainer": {
        padding: "12px 12px 0",
        gap: 8,
    },
};

export const scheduleTableTypography = {
    table: {
        fontFamily: defaultTableFontFamily,
        fontSize: "0.875rem",
        lineHeight: 1.45,
        color: schedulePalette.ink,
    },
    headerCell: {
        fontSize: "0.82rem",
        fontWeight: 700,
        letterSpacing: "0.01em",
    },
    bodyCell: {
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: 1.45,
        color: "var(--app-table-cell-text-color, #3498db)",
    },
    sectionCell: {
        fontSize: "0.9rem",
        fontWeight: 700,
        lineHeight: 1.35,
        color: "var(--app-table-cell-text-color, #3498db)",
    },
    sectionMeta: {
        fontSize: "0.72rem",
        fontWeight: 500,
        lineHeight: 1.3,
    },
    employeeName: {
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: 1.4,
        color: "var(--app-table-cell-text-color, #3498db)",
    },
    helper: {
        fontSize: "0.78rem",
        fontWeight: 500,
        lineHeight: 1.4,
    },
};

export const pageTypography = {
    pageTitle: {
        fontFamily: defaultAppFontFamily,
        fontSize: { xs: "1.85rem", md: "2.15rem" },
        fontWeight: 800,
        lineHeight: 1.08,
        letterSpacing: "0.01em",
        color: schedulePalette.ink,
    },
    pageSubtitle: {
        fontFamily: defaultAppFontFamily,
        fontSize: "0.96rem",
        fontWeight: 500,
        lineHeight: 1.6,
        color: schedulePalette.muted,
    },
    panelTitle: {
        fontFamily: defaultAppFontFamily,
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: 1.35,
        color: schedulePalette.ink,
    },
    panelLabel: {
        fontFamily: defaultAppFontFamily,
        fontSize: "0.82rem",
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: "0.01em",
        color: schedulePalette.muted,
    },
    panelValue: {
        fontFamily: defaultAppFontFamily,
        fontSize: { xs: "1.85rem", md: "2rem" },
        fontWeight: 800,
        lineHeight: 1.1,
        color: schedulePalette.ink,
    },
    panelCaption: {
        fontFamily: defaultAppFontFamily,
        fontSize: "0.78rem",
        fontWeight: 500,
        lineHeight: 1.45,
        color: schedulePalette.muted,
    },
    bodyText: {
        fontFamily: defaultAppFontFamily,
        fontSize: "0.9rem",
        fontWeight: 500,
        lineHeight: 1.55,
        color: schedulePalette.ink,
    },
};

export const createTableTypography = (fontFamily) => ({
    ...scheduleTableTypography,
    table: {
        ...scheduleTableTypography.table,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    headerCell: {
        ...scheduleTableTypography.headerCell,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    bodyCell: {
        ...scheduleTableTypography.bodyCell,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    sectionCell: {
        ...scheduleTableTypography.sectionCell,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    sectionMeta: {
        ...scheduleTableTypography.sectionMeta,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    employeeName: {
        ...scheduleTableTypography.employeeName,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
    helper: {
        ...scheduleTableTypography.helper,
        fontFamily: fontFamily || defaultTableFontFamily,
    },
});